import { useEffect } from "react";
import { useMst } from "../stores/main";
import { isKeyboardShortcutSuppressedTarget } from "./keyboardShortcutGuards";

const BASE_NUDGE_AMOUNT = 1;
const SHIFT_NUDGE_AMOUNT = 10;

type LayerNudgeDelta = {
  x: number;
  y: number;
};

function stopHandledNudgeEvent(e: KeyboardEvent): void {
  e.preventDefault();
  e.stopPropagation();
  if (typeof e.stopImmediatePropagation === "function") {
    e.stopImmediatePropagation();
  }
}

export function getLayerNudgeDelta(
  event: Pick<
    KeyboardEvent,
    "key" | "shiftKey" | "altKey" | "metaKey" | "ctrlKey"
  >
): LayerNudgeDelta | undefined {
  if (event.altKey || event.metaKey || event.ctrlKey) {
    return undefined;
  }

  const amount = event.shiftKey ? SHIFT_NUDGE_AMOUNT : BASE_NUDGE_AMOUNT;

  switch (event.key) {
    case "ArrowUp":
      return { x: 0, y: -amount };
    case "ArrowDown":
      return { x: 0, y: amount };
    case "ArrowLeft":
      return { x: -amount, y: 0 };
    case "ArrowRight":
      return { x: amount, y: 0 };
    default:
      return undefined;
  }
}

/**
 * Hook that sets up keyboard shortcuts for nudging the selected layer:
 * - Arrow keys move the selected layer by 1 composition unit in design mode
 * - Shift+Arrow moves it by 10 composition units
 */
export function useLayerNudgeShortcuts() {
  const { canvasTransformStore, currentTab, playerStore, undoStore } = useMst();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const delta = getLayerNudgeDelta(e);
      if (!delta) {
        return;
      }

      if (isKeyboardShortcutSuppressedTarget(e.target)) {
        return;
      }

      if (
        currentTab !== "layers" ||
        !playerStore.layerInspectorEnabled ||
        playerStore.layerInspectorMode !== "design" ||
        !playerStore.layerInspectorPrimarySelection ||
        undoStore.isGestureActive
      ) {
        return;
      }

      stopHandledNudgeEvent(e);
      canvasTransformStore.nudgeSelection(delta.x, delta.y);
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [canvasTransformStore, currentTab, playerStore, undoStore]);
}
